import styled from "styled-components";

export const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 75vh;
  overflow-y: auto;
`;

export const AnalyticsSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  color: white;
`;

export const AnalyticsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h4 {
    color: white;
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    opacity: 0.9;
  }
`;

export const AnalyticsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

export const AnalyticsCard = styled.div<{ $variant?: "primary" | "secondary" }>`
  background: ${(props) =>
    props.$variant === "secondary"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(255, 255, 255, 0.15)"};
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const AnalyticsLabel = styled.span`
  font-size: 11px;
  opacity: 0.85;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const AnalyticsValue = styled.span`
  font-size: 18px;
  font-weight: 700;
`;

export const RatioBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 8px;
  background: rgba(255, 255, 255, 0.2);
`;

export const RatioSegment = styled.div<{ $width: number; $color: string }>`
  width: ${(props) => props.$width}%;
  background: ${(props) => props.$color};
  transition: width 0.3s ease;
`;

export const RatioLegend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 11px;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.$color};
`;

export const DetailsSection = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
`;

export const DetailsSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

export const DetailLabel = styled.span`
  color: #6b7280;
  font-size: 13px;
`;

export const DetailValue = styled.span<{ $type?: "success" | "warning" | "danger" | "primary" }>`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => {
    switch (props.$type) {
      case "success":
        return "#10b981";
      case "warning":
        return "#f59e0b";
      case "danger":
        return "#ef4444";
      case "primary":
        return "#6366f1";
      default:
        return "#374151";
    }
  }};
`;

export const PatientSection = styled.div`
  background: #f0f9ff;
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid #3b82f6;
`;

export const PatientHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const PatientAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

export const PatientInfo = styled.div`
  flex: 1;

  .patient-name {
    font-weight: 600;
    font-size: 16px;
    color: #1e40af;
  }

  .patient-id {
    font-size: 12px;
    color: #6b7280;
  }
`;

export const OperationsSection = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
`;

export const OperationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
`;

export const OperationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
`;

export const OperationName = styled.span`
  font-weight: 500;
  color: #374151;
  flex: 1;
`;

export const OperationCode = styled.span`
  color: #6b7280;
  font-size: 11px;
  margin-left: 8px;
`;

export const OperationPrice = styled.span`
  font-weight: 600;
  color: #059669;
`;

export const StatusBadge = styled.span<{ $status?: string }>`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-right: 8px;
  background: ${(props) => {
    switch (props.$status) {
      case "COMPLETED":
        return "#dcfce7";
      case "IN_PROGRESS":
        return "#fef3c7";
      case "PLANNED":
        return "#dbeafe";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "COMPLETED":
        return "#166534";
      case "IN_PROGRESS":
        return "#92400e";
      case "PLANNED":
        return "#1e40af";
      default:
        return "#4b5563";
    }
  }};
`;

export const PaymentsSection = styled.div`
  background: #f0fdf4;
  border-radius: 12px;
  padding: 16px;
  border-left: 4px solid #10b981;
`;

export const PaymentsSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const PaymentTotal = styled.div`
  .label {
    font-size: 12px;
    color: #6b7280;
  }
  .amount {
    font-size: 24px;
    font-weight: 700;
    color: #059669;
  }
`;

export const PaymentRemaining = styled.div`
  text-align: right;
  .label {
    font-size: 12px;
    color: #6b7280;
  }
  .amount {
    font-size: 18px;
    font-weight: 600;
    color: #f59e0b;
  }
`;

export const PaymentProgressBar = styled.div`
  height: 12px;
  background: #d1fae5;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

export const PaymentProgress = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(props) => Math.min(props.$percent, 100)}%;
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
`;

export const PaymentProgressLabel = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  font-weight: 600;
  color: #166534;
`;

export const FooterActions = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 12px;
  color: #ef4444;
`;

export const EInvoiceSection = styled.div`
  display: flex;
  gap: 12px;
`;

export const EInvoiceCard = styled.div<{ $submitted?: boolean; $fullWidth?: boolean }>`
  flex: ${(props) => (props.$fullWidth ? "none" : "1")};
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
  padding: 12px;
  background: ${(props) => (props.$submitted ? "#f0fdf4" : "#fef2f2")};
  border-radius: 8px;
  border: 1px solid ${(props) => (props.$submitted ? "#bbf7d0" : "#fecaca")};
  text-align: center;

  .label {
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .status {
    font-size: 12px;
    font-weight: 600;
    color: ${(props) => (props.$submitted ? "#166534" : "#b91c1c")};
  }
`;

